<template>
  <div class="c-chat__messagesContainer">
    <ChatPushPermissionPrompt
      v-if="showPushPrompt" />
    <ChatAutoTranslatePrompt
      v-if="showAutoTranslatePrompt" />
    <DynamicScroller
      v-show="chatThreadStore.sortedMessages?.length && !chatThreadStore.chatThreadIsOpening"
      ref="messagesScroller"
      :items="chatThreadStore.sortedMessages"
      :min-item-size="50"
      class="c-chat__messagesScroller">
      <template #default="{ item, index, active }">
        <DynamicScrollerItem
          :item="item"
          :active="active"
          :size-dependencies="[
            item.content,
            item.metadata?.state,
            item.metadata?.transcript,
            item.showTranslation,
            item.translatedText,
            chatThreadStore.expandedReplyPreviews[item.id],
          ]"
          :data-index="index"
          :data-message-id="item.id"
          class="c-chat__messageRow"
          :class="{ '-me': isMe(item.senderDisplayName) }">
          <div>
            <div class="o-flex -alignCenter -gap4">
              <button
                v-if="isMe(item.senderDisplayName) && item.metadata?.state === constants.CHAT.STATES.ERROR && [constants.CHAT.MESSAGE_ERRORS.FAILED_TO_SAVE, constants.CHAT.MESSAGE_ERRORS.FAILED_TO_SEND].includes(item.metadata?.error_status)"
                class="c-button -icon -tiny u-colorError"
                @click="retrySendMessage(item)">
                <SvgIcon
                  small
                  type="chat_resend" />
              </button>
              <button
                v-if="isMe(item.senderDisplayName) && item.metadata?.state === constants.CHAT.STATES.ERROR && item.metadata?.error_status === constants.CHAT.MESSAGE_ERRORS.FAILED_TO_UPLOAD"
                class="c-button -icon -tiny u-colorError"
                @click="deleteFailedUploadMessage(item)">
                <SvgIcon
                  small
                  type="close" />
              </button>
              <Message
                :key="item.id"
                :message="item"
                @delete="openDeleteMessageDialog" />
            </div>
          </div>
        </DynamicScrollerItem>
      </template>
    </DynamicScroller>
    <template v-if="!chatThreadStore.sortedMessages?.length && !chatThreadStore.chatThreadIsOpening">
      <div class="u-marginRight4 u-marginLeft4">
        <h5 class="t-body3">{{ $t('chat.thread_has_no_messages_title') }}</h5>
        <UserData
          class="s-chat__messagesLoaderWrapper o-flex -alignCenter -center -column"
          :user="chatStore.chatThread.participant" />
      </div>
    </template>
    <div
      v-if="chatThreadStore.chatThreadIsOpening"
      class="s-chat__messagesLoaderWrapper o-flex -alignCenter -center">
      <LoadingSpinner />
    </div>
    <ConfirmationModal
      v-model="deletionMessageModalIsOpen"
      :title="$t('chat.delete_chat.title')"
      :content="$t('chat.delete_chat.content')"
      :confirm-button-text="$t('chat.delete_chat.confirm_deletion')"
      confirm-button-class="-error"
      @confirm="deleteMessage" />
  </div>
</template>

<script setup>

import {
  defineProps, ref, onMounted, onBeforeUnmount, nextTick, watch, computed,
} from 'vue'
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'

import ConfirmationModal from '@/components/ConfirmationModal.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import constants from '@/constants'
import api from '@/logic/api'
import azureChatApi from '@/modules/chat/api/azureChat'
import chatApi from '@/modules/chat/api/chat'
import { useChatAttachmentsStore } from '@/modules/chat/chatAttachmentsStore'
import { useChatStore } from '@/modules/chat/chatStore'
import { useChatThreadStore } from '@/modules/chat/chatThreadStore'
import ChatAutoTranslatePrompt from '@/modules/chat/components/ChatAutoTranslatePrompt.vue'
import ChatPushPermissionPrompt from '@/modules/chat/components/ChatPushPermissionPrompt.vue'
import Message from '@/modules/chat/components/Message.vue'
import UserData from '@/modules/chat/components/UserProfile/UserData.vue'
import { getParticipant } from '@/modules/chat/logic/chatThread'
import store from '@/store'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'

const props = defineProps({
  chatThread: {
    type: Object,
    required: true,
  },
})

const chatStore = useChatStore()
const chatThreadStore = useChatThreadStore(props.chatThread.azure_chat_thread_id)
const chatAttachmentsStore = useChatAttachmentsStore(props.chatThread.azure_chat_thread_id)

let olderMessagesAreLoading = false
const messagesScroller = ref()
const deletionMessageModalIsOpen = ref(false)
const messageToDelete = ref(null)
const resetDeletingCallback = ref(null)
const isDeleteInProgress = ref(false)

const showPushPrompt = computed(() => {
  if (window.Capacitor) {
    return false
  }
  if (typeof Notification === 'undefined') {
    return false
  }
  return chatStore.shouldShowNotificationPrompt
})

const showAutoTranslatePrompt = computed(() => {
  if (chatStore.chatThread?.auto_translate_enabled === true) {
    return false
  }
  return !!chatThreadStore.showAutoTranslatePrompt
})

function updateScrollbarPosition(messageId) {
  const newItemIndex = messagesScroller.value.items.findIndex((item) => item.id === messageId)
  return messagesScroller.value.scrollToItem(newItemIndex)
}

function isMe(userId) {
  return parseInt(userId) === store.getters['auth/userId']
}

async function setReadReceiptForNewestMessage() {
  const newestMessageId = chatThreadStore.sortedMessages[chatThreadStore.sortedMessages.length - 1]?.id.toString()

  if (!chatThreadStore.myReadReceipt || chatThreadStore.myReadReceipt.chatMessageId !== newestMessageId) {
    // It sets a reading receipt when a user open a chat thread and last messages have been fetched
    await chatThreadStore.setReadReceipt(newestMessageId)
    try {
      await chatThreadStore.loadReadReceipts(true)
    } catch (error) {
      console.error('Failed to load read receipts:', error)
    }
  }
  chatStore.updateHasUnreadMessagesInThreads(props.chatThread.azure_chat_thread_id)
}
async function init() {
  await chatThreadStore.init(messagesScroller)
  setReadReceiptForNewestMessage()
    .catch((error) => {
      console.error('Failed to set read receipt for newest message:', error)
    })
  chatThreadStore.observeResize()
  await nextTick()
  chatThreadStore.chatThreadIsOpening = false
}
init()

const isMessagesScrollerAtBottom = () => {
  if (!messagesScroller.value) return true

  const scrollBottom = messagesScroller.value.$el.scrollTop + messagesScroller.value.$el.clientHeight
  const scrollHeight = messagesScroller.value.$el.scrollHeight

  return scrollBottom >= scrollHeight - 1 // small margin for floating point accuracy
}

async function onScroll() {
  const isAtBottom = isMessagesScrollerAtBottom()
  const isAtBottomOldValue = chatThreadStore.isScrollerAtBottom
  // To fix getting a new messages when you're at bottom position and scrolled to up
  // This will reflect the unread messages status in the chat thread immediately
  if (isAtBottomOldValue && !isAtBottom) {
    chatStore.threadsHasUnreadMessages[props.chatThread.azure_chat_thread_id] = false
  }
  chatThreadStore.isScrollerAtBottom = isAtBottom
  if (isAtBottom) {
    const newestMessageId = chatThreadStore.sortedMessages[chatThreadStore.sortedMessages.length - 1]?.id.toString()

    if (!chatThreadStore.myReadReceipt || chatThreadStore.myReadReceipt.chatMessageId !== newestMessageId) {
      // It sets a reading receipt when a user open a chat thread and last messages have been fetched
      await chatThreadStore.setReadReceipt(newestMessageId)
    }
    if (chatThreadStore.unSeenMessages) {
      chatStore.updateHasUnreadMessagesInThreads(props.chatThread.azure_chat_thread_id)
      chatThreadStore.unSeenMessages = 0
    }
  }

  if (olderMessagesAreLoading || chatThreadStore.chatThreadIsOpening || chatThreadStore.allMessagesAreLoaded) {
    return
  }
  if (messagesScroller.value.$el.scrollTop > 0) {
    return
  }

  olderMessagesAreLoading = true

  // we always want to scroll to the message which was on the top message before loading messages
  const messageId = chatThreadStore.sortedMessages[0].id

  await chatThreadStore.loadMoreMessages()
  await updateScrollbarPosition(messageId)

  olderMessagesAreLoading = false
}

const retrySendMessage = async (message) => {
  if (message.metadata.error_status === constants.CHAT.MESSAGE_ERRORS.FAILED_TO_SAVE) {
    const serverId = chatAttachmentsStore.getFileIdByMessageId(message.id)
    if (serverId) {
      chatAttachmentsStore.attachments[serverId] = message.id.toString()
      await chatAttachmentsStore.saveAttachmentFile(serverId)
      await chatThreadStore.updateSortedMessages()
    }
  }
  if (message.metadata.error_status === constants.CHAT.MESSAGE_ERRORS.FAILED_TO_SEND) {
    try {
      message.metadata.state = constants.CHAT.STATES.SENT
      const sendMessageResult = await azureChatApi.sendMessage(chatStore.chatThread.azure_chat_thread_id, message.content, message.metadata)
      chatApi.notifyAboutNewMessage({
        body: message.content,
        chatThreadId: chatStore.chatThread.id,
        participantId: getParticipant(chatStore.chatThread)?.user_id,
        messageId: sendMessageResult.id,
      })
      azureChatApi.setReadingEvent(chatStore.chatThread.azure_chat_thread_id, sendMessageResult.id)
      chatStore.setChatThreadLastMessage(chatStore.chatThread.azure_chat_thread_id)
    } catch (error) {
      message.metadata.state = constants.CHAT.STATES.ERROR
      message.metadata.error_status = constants.CHAT.MESSAGE_ERRORS.FAILED_TO_SEND
      await chatThreadStore.updateSortedMessages()
      store.commit('notifications/addError', {
        title: api.i18n.t('notifications.failed_to_send_message'),
      })
    }
  }
}

/**
 * Delete a failed upload message immediately without confirmation.
 * Uses the same deletion logic as cancel upload.
 */
const deleteFailedUploadMessage = async (message) => {
  if (message.metadata?.error_status !== constants.CHAT.MESSAGE_ERRORS.FAILED_TO_UPLOAD) {
    return
  }

  const fileId = chatAttachmentsStore.getFileIdByMessageId(message.id)

  try {
    // Use the shared cancel/delete action from attachments store
    await chatAttachmentsStore.cancelUploadOrDeleteMessage(fileId, message.id)
  } catch (error) {
    console.error('Failed to delete failed upload message:', error)
  }
}

const openDeleteMessageDialog = async (message, resetCallback) => {
  messageToDelete.value = message
  resetDeletingCallback.value = resetCallback
  deletionMessageModalIsOpen.value = true
}

const deleteMessage = async () => {
  isDeleteInProgress.value = true
  const currentResetCallback = resetDeletingCallback.value
  resetDeletingCallback.value = null
  try {
    await azureChatApi.deleteMessage(chatStore.openAzureChatThreadId, messageToDelete.value.id)
    chatThreadStore.messages = chatThreadStore.messages.filter((threadMessage) => +threadMessage.id !== +messageToDelete.value.id)
    chatThreadStore.allLoadedMessages = chatThreadStore.allLoadedMessages.filter((threadMessage) => +threadMessage.id !== +messageToDelete.value.id)
    await chatThreadStore.updateSortedMessages()
    await chatStore.setChatThreadLastMessage(chatStore.openAzureChatThreadId)
    await chatStore.updateHasUnreadMessagesInThreads(chatStore.openAzureChatThreadId)
    if (currentResetCallback) {
      currentResetCallback()
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to delete message:', error)
    store.commit('notifications/addError', {
      title: api.i18n.t('errors.delete_failed'),
    })
    if (currentResetCallback) {
      currentResetCallback()
    }
  } finally {
    isDeleteInProgress.value = false
    messageToDelete.value = null
  }
}

watch(deletionMessageModalIsOpen, (isOpen) => {
  // When modal closes without confirm (cancel), reset the deleting state
  // Skip if delete is in progress (modal closes before @confirm fires)
  if (!isOpen && resetDeletingCallback.value && !isDeleteInProgress.value) {
    resetDeletingCallback.value()
    resetDeletingCallback.value = null
  }
})

onMounted(async () => {
  chatStore.openAzureChatThreadId = props.chatThread.azure_chat_thread_id
  messagesScroller.value.$el.addEventListener('scroll', onScroll)
  watch(() => chatThreadStore?.typingIndicator, () => {
    const isAtBottom = isMessagesScrollerAtBottom()
    chatStore.updateMessagesContainerHeight(!!chatThreadStore?.typingIndicator)
    nextTick(() => {
      if (isAtBottom) {
        chatThreadStore.scrollContainerDown()
      }
    })
  })
})

onBeforeUnmount(() => {
  chatStore.openAzureChatThreadId = undefined
  messagesScroller.value.$el.removeEventListener('scroll', onScroll)
})

defineExpose({
  messagesScroller,
  isMessagesScrollerAtBottom,
})
</script>

<style scoped lang="scss">
.s-chat__messagesLoaderWrapper {
  position: absolute;
  inset: 0;
}
</style>
